// auth.ts — contractor auth for Portal (EGO-95).
//
// Owns the whole signed-out → signed-in lifecycle and gates the Portal UI:
//   1. signed out            → login card (email/password or magic link)
//   2. signed in, incomplete → "finish your account" card (invited users land
//        here from the invite email: set full name + password, accept ToS —
//        tos_accepted_at is the consent moment, these are in-home recordings)
//   3. complete              → Portal app + account header + bound-devices list
//
// Supabase signups are disabled project-wide: accounts exist only via invites
// (dashboard or service-role inviteUserByEmail). The invite email lands on
// this page with tokens in the URL hash; supabase-js picks them up itself
// (detectSessionInUrl) and fires onAuthStateChange.
//
// The anon key is public by design — RLS is the security boundary. Privileged
// operations (device link codes, presigned uploads) go through the Worker at
// https://api.egocentric.com, never straight to Supabase.
import { createClient, type Session } from "@supabase/supabase-js";
// Cycle note: apk.ts imports { supabase } from here; both sides only touch the
// other's exports inside functions, so the esbuild cycle is harmless.
import { WORKER_URL } from "./apk.ts";

export const SUPABASE_URL = "https://nypzntvhgvgxjjvpftwt.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im55cHpudHZoZ3ZneGpqdnBmdHd0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODM5NzM5NzMsImV4cCI6MjA5OTU0OTk3M30.ZvBEYlpNXBCQ6lRJepVHZSWNrgqet3Aqbn-TZ3bDgIU";

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

type Profile = {
  id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  tos_accepted_at: string | null;
  onboarding_step: string;
};

const $ = (id: string) => document.getElementById(id)!;

export interface AuthHandlers {
  /** boot the full Portal app (signed in, profile complete, onboarding done) */
  bootApp(): void;
  /** start/resume the onboarding wizard at the given profiles.onboarding_step */
  startOnboarding(step: string): void;
}

let booted = false;          // start the Portal app at most once per page load
let currentProfile: Profile | null = null;
let handlers: AuthHandlers;

export function getProfile(): Profile | null { return currentProfile; }

// ── view switching ──────────────────────────────────────────────────────────

// Signed-out users see the LANDING (light, no app chrome) by default; the
// sign-in card lives behind #signin and the (mock) signup behind #signup —
// people on the landing page mostly don't have accounts yet.
function show(view: "landing" | "login" | "signup" | "setup" | "app" | "wizard") {
  $("authView").style.display = view === "login" || view === "setup" ? "" : "none";
  $("landing").style.display = view === "landing" ? "" : "none";
  $("signupView").style.display = view === "signup" ? "" : "none";
  $("loginCard").style.display = view === "login" ? "" : "none";
  $("setupCard").style.display = view === "setup" ? "" : "none";
  $("appMain").style.display = view === "app" ? "" : "none";
  $("wizard").style.display = view === "wizard" ? "" : "none";
  $("accountBox").style.display = view === "landing" || view === "login" || view === "signup" ? "none" : "";
  // Signup keeps the landing's light styling — it's the same funnel; the dark
  // chrome starts once you're in the app. The setup card is funnel too: no
  // header, vertically centered.
  document.body.classList.toggle("landing", view === "landing" || view === "signup");
  $("authView").classList.toggle("centered", view === "setup" || view === "login");
}

function authMsg(text: string, isError = false) {
  const el = $("authMsg");
  el.textContent = text;
  el.style.color = isError ? "var(--err)" : "var(--muted)";
}

// ── state machine ───────────────────────────────────────────────────────────

async function render(session: Session | null) {
  if (!session) {
    currentProfile = null;
    show(location.hash === "#signin" ? "login" : location.hash === "#signup" ? "signup" : "landing");
    return;
  }

  $("accountEmail").textContent = session.user.email ?? "";

  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, display_name, is_admin, tos_accepted_at, onboarding_step")
    .eq("id", session.user.id)
    .single();
  if (error || !data) {
    // Profile rows are trigger-created with the auth user; missing means
    // something is genuinely wrong — don't let a broken account into the app.
    authMsg(`account error: ${error?.message ?? "no profile"}`, true);
    show("login");
    return;
  }
  currentProfile = data as Profile;

  if (!currentProfile.display_name || !currentProfile.tos_accepted_at) {
    ($("setupName") as HTMLInputElement).value = currentProfile.display_name ?? "";
    show("setup");
    return;
  }

  // Contractors go through onboarding before they see the Portal UI (EGO-99).
  // Admins are internal — never wizarded automatically. ANY signed-in user can
  // opt back in with #wizard (footer "Setup guide"; admins get the jump/reset
  // strip, finished contractors land on the last step with redo links).
  if ((!currentProfile.is_admin && currentProfile.onboarding_step !== "done") ||
      location.hash === "#wizard") {
    show("wizard");
    handlers.startOnboarding(currentProfile.onboarding_step);
    return;
  }

  show("app");
  refreshDevices();
  if (!booted) { booted = true; handlers.bootApp(); }
}

// ── bound devices (EGO-97 populates these via the Worker) ───────────────────

async function refreshDevices() {
  const box = $("deviceRows");
  const { data, error } = await supabase
    .from("devices")
    .select("id, label, serial, created_at, last_seen_at")
    .is("revoked_at", null)
    .order("created_at", { ascending: false });
  if (error) { box.innerHTML = `<tr><td colspan="4" style="color:var(--err)">${error.message}</td></tr>`; return; }
  if (!data.length) {
    box.innerHTML = `<tr><td colspan="4" style="color:var(--muted)">No headsets bound yet — onboarding binds one.</td></tr>`;
    return;
  }
  box.innerHTML = data.map((d) => `<tr>
    <td>${d.label ?? d.id.slice(0, 8)}${d.serial ? `<span style="color:var(--muted); font-size:11px; margin-left:6px;">${d.serial}</span>` : ""}</td>
    <td class="num">${new Date(d.created_at).toLocaleDateString()}</td>
    <td class="num">${d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "never"}</td>
    <td><button class="secondary" data-revoke="${d.id}">Disconnect</button></td>
  </tr>`).join("");
  for (const b of box.querySelectorAll<HTMLButtonElement>("[data-revoke]")) {
    b.addEventListener("click", async () => {
      if (!confirm("Disconnect this headset? It will refuse to record until re-linked.")) return;
      const { error: e } = await supabase.from("devices")
        .update({ revoked_at: new Date().toISOString() }).eq("id", b.dataset.revoke!);
      if (e) alert(`disconnect failed: ${e.message}`);
      refreshDevices();
    });
  }
}

// ── entry point ─────────────────────────────────────────────────────────────

export function initAuth(h: AuthHandlers) {
  handlers = h;
  // These cards aren't <form>s (no page-reload semantics wanted), so Enter has
  // to be wired by hand or it silently does nothing in the password field.
  const onEnter = (id: string, btn: string) =>
    $(id).addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key !== "Enter") return;
      e.preventDefault();
      $(btn).click();
    });

  // Login: password
  $("btnLogin").addEventListener("click", async () => {
    const email = ($("loginEmail") as HTMLInputElement).value.trim();
    const password = ($("loginPassword") as HTMLInputElement).value;
    if (!email || !password) return authMsg("email and password required", true);
    authMsg("signing in…");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) authMsg(error.message, true); else authMsg("");
  });

  // Login: magic link (existing accounts only — signups are invite-only)
  $("btnMagic").addEventListener("click", async () => {
    const email = ($("loginEmail") as HTMLInputElement).value.trim();
    if (!email) return authMsg("enter your email first", true);
    authMsg("sending magic link…");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: location.origin },
    });
    authMsg(error ? error.message : "magic link sent — check your email", !!error);
  });

  onEnter("loginEmail", "btnLogin");
  onEnter("loginPassword", "btnLogin");

  // Finish account (invite landing): name + password + ToS consent
  $("btnSetup").addEventListener("click", async () => {
    const name = ($("setupName") as HTMLInputElement).value.trim();
    const password = ($("setupPassword") as HTMLInputElement).value;
    const tos = ($("setupTos") as HTMLInputElement).checked;
    const msg = $("setupMsg");
    const fail = (t: string) => { msg.textContent = t; msg.style.color = "var(--err)"; };
    if (!name) return fail("your full name is required");
    if (password && password.length < 8) return fail("password must be at least 8 characters");
    if (!tos) return fail("you must accept the Terms of Service to continue");
    msg.textContent = "saving…"; msg.style.color = "var(--muted)";

    if (password) {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) return fail(error.message);
    }
    const uid = (await supabase.auth.getUser()).data.user?.id;
    const { error } = await supabase.from("profiles").update({
      display_name: name,
      tos_accepted_at: new Date().toISOString(),
    }).eq("id", uid!);
    if (error) return fail(error.message);
    const { data: { session } } = await supabase.auth.getSession();
    render(session);
  });

  onEnter("setupName", "btnSetup");
  onEnter("setupPassword", "btnSetup");

  $("btnSignOut").addEventListener("click", () => supabase.auth.signOut());

  // Landing (logged-out explainer): Get Started routes to (mock) signup.
  $("btnLandingStart").addEventListener("click", () => { location.hash = "#signup"; });
  $("loginBack").addEventListener("click", (e) => { e.preventDefault(); location.hash = ""; });

  // Request access (invite-only): the card doesn't create accounts — it files
  // the email into interest_emails via the public worker endpoint. Accounts
  // exist only via Supabase invites; the invite email link IS the signup.
  const submitInterest = async (email: string, source: string, say: (t: string, err?: boolean) => void) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return say("that doesn't look like an email address", true);
    say("sending…");
    try {
      const r = await fetch(`${WORKER_URL}/interest`, {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      if (r.status === 429) return say("too many tries — give it a minute", true);
      if (!r.ok) throw new Error(String(r.status));
      say("✓ You're on the list — we'll reach out when a spot opens.");
    } catch {
      say("couldn't reach the server — try again in a moment", true);
    }
  };
  $("btnSignup").addEventListener("click", () => {
    const msg = $("signupMsg");
    void submitInterest(
      ($("signupEmail") as HTMLInputElement).value.trim(), "request-access",
      (t, err = false) => { msg.textContent = t; msg.style.color = err ? "var(--err)" : "var(--muted)"; });
  });

  onEnter("signupEmail", "btnSignup");
  onEnter("interestEmail", "btnInterest");

  // "Keep me posted" popup — intentionally non-functional for now.
  const interestModal = $("interestModal");
  $("interestOpen").addEventListener("click", (e) => {
    e.preventDefault();
    $("interestMsg").textContent = "";
    interestModal.style.display = "flex";
    ($("interestEmail") as HTMLInputElement).focus();
  });
  $("interestClose").addEventListener("click", () => { interestModal.style.display = "none"; });
  interestModal.addEventListener("click", (e) => { if (e.target === interestModal) interestModal.style.display = "none"; });
  // Hash is the signed-out router (landing ↔ #signin) and the admin #wizard
  // toggle. Supabase auth-token hashes (#access_token=…) are supabase-js's —
  // never re-render on those, onAuthStateChange handles them.
  window.addEventListener("hashchange", async () => {
    if (location.hash.includes("access_token")) return;
    render((await supabase.auth.getSession()).data.session);
    if (location.hash === "#signin") ($("loginEmail") as HTMLInputElement).focus();
  });
  $("btnInterest").addEventListener("click", () => {
    const msg = $("interestMsg");
    void submitInterest(
      ($("interestEmail") as HTMLInputElement).value.trim(), "landing-popup",
      (t, err = false) => { msg.textContent = t; msg.style.color = err ? "var(--err)" : "var(--muted)"; });
  });

  supabase.auth.onAuthStateChange((_event, session) => { render(session); });
}
